// src/components/ConnectionsModal.js

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "api";
import { CheckCircle, X } from "phosphor-react";
import { DEFAULT_CIRCLE_IMAGE, DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";

const toAvatar = (u) =>
  u?.profile_pic_url || u?.avatar_url || u?.photo_url || u?.image_url || DEFAULT_PROFILE_IMAGE;

/** Pull a user object out of many possible server shapes */
function pickUser(row) {
  const u =
    row?.user ||
    row?.member ||
    row?.following ||
    row?.follower ||
    row?.addressee ||
    row?.receiver ||
    row?.requester ||
    row?.sender ||
    row; // fall back to row itself

  const name =
    u?.display_name ||
    u?.name ||
    [u?.first_name, u?.last_name].filter(Boolean).join(" ") ||
    u?.username ||
    (u?.id ? `User #${u.id}` : "User");

  return {
    id: u?.id,
    name,
    username: u?.username,
    profile_pic_url: u?.profile_pic_url || u?.avatar_url || u?.photo_url || u?.image_url || null,
    avatar_url: u?.avatar_url || null,
  };
}

function normalizeList(data) {
  const arr =
    Array.isArray(data)
      ? data
      : Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data?.users)
      ? data.users
      : [];
  return arr.map(pickUser).filter((u) => u?.id != null);
}

/* =========================================================================
   CARD (preview) — opens the modal internally
   ========================================================================= */

export function ConnectionsCard({
  meId,
  ownerName,     // <<— profile owner's display name (for title)
  viewerId,       // <<— current viewer's id (for status in modal)
  initialTab = "overview",
  hideTitle = false,
  frameless = false,
}) {
  const [open, setOpen] = useState(false);
  const [modalTab, setModalTab] = useState(initialTab);
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);

  const headers = useMemo(() => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token") || null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchFollowing = async () => {
    if (!meId) return setFollowing([]);
    try {
      const { data } = await api.get(`/connections/following/${meId}`, { headers });
      setFollowing(normalizeList(data));
    } catch {
      setFollowing([]);
    }
  };

  const fetchFollowers = async () => {
    if (!meId) return setFollowers([]);
    try {
      const { data } = await api.get(`/connections/followers/${meId}`, { headers });
      setFollowers(normalizeList(data));
    } catch {
      setFollowers([]);
    }
  };

  useEffect(() => {
    fetchFollowing();
    fetchFollowers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meId]);

  const openModalOn = (tab) => {
    setModalTab(tab);
    setOpen(true);
  };

  const outerClass = frameless ? "" : "bg-white rounded-2xl px-5 py-4";
  const sectionTitleClass = "text-sm text-gray-700 ml-2 mb-2";

  const StackAvatars = ({ users, size = 10, max = 5 }) => {
    const shown = users.slice(0, max);
    const remaining = users.length - shown.length;
    const px = `w-${size} h-${size}`;
    return (
      <div className="flex -space-x-3 justify-start ml-2">
        {shown.map((u) => (
          <img
            key={u.id}
            src={toAvatar(u)}
            alt={u.name || `User #${u.id}`}
            className={`${px} rounded-full object-cover ring-2 ring-white`}
            draggable={false}
          />
        ))}
        {remaining > 0 && (
          <div
            className={`${px} rounded-full ring-2 ring-white bg-gray-100 grid place-items-center text-xs text-gray-600`}
          >
            +{remaining}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={outerClass}>
      {!hideTitle && (
        <h4 className="font-semibold text-[1.1rem]">
            {ownerName
              ? `${ownerName}'s Connections`
              : (viewerId && meId && String(viewerId) === String(meId)
                  ? "Your Connections"
                  : "Connections")}
        </h4>
      )}

      {/* Following */}
      <section className={`${hideTitle || frameless ? "" : "!mt-3"}`}>
        <div className={sectionTitleClass}>Following</div>
        {following.length > 0 ? (
          <button
            type="button"
            onClick={() => openModalOn("following")}
            className="block !min-h-0 !min-w-0 text-left group w-full"
          >
            <StackAvatars users={following} size={10} max={5} />
            <div className="mt-2 ml-2 text-sm text-blue-600 group-hover:font-bold">
              {following.length} following
            </div>
          </button>
        ) : (
          <div className="ml-2 text-xs text-gray-400">No one yet</div>
        )}
      </section>

      {/* Followers */}
      <section className="!mt-2">
        <div className={sectionTitleClass}>Followers</div>
        {followers.length > 0 ? (
          <button
            type="button"
            onClick={() => openModalOn("followers")}
            className="block text-left group w-full"
          >
            <StackAvatars users={followers} size={10} max={5} />
            <div className="mt-2 ml-2 text-sm text-blue-600 group-hover:font-bold">
              {followers.length} follower{followers.length === 1 ? "" : "s"}
            </div>
          </button>
        ) : (
          <div className="ml-2 text-xs text-gray-400">No followers yet</div>
        )}
      </section>

      {open && (
        <ConnectionsModal
          open
          onClose={() => setOpen(false)}
          meId={meId}
          viewerId={viewerId}
          ownerName={ownerName}
          initialTab={modalTab}
        />
      )}
    </div>
  );
}

/* =========================================================================
   MODAL
   ========================================================================= */
export default function ConnectionsModal({
  open,
  onClose,
  meId,        // profile owner id (whose list we are viewing)
  viewerId,    // current viewer id (to compute YOUR relationship)
  ownerName,   // profile owner's display name for titles
  initialTab = "overview",
  listOnly = false,
}) {
  const [tab, setTab] = useState(initialTab);
  const [following, setFollowing] = useState([]);          // meId's following
  const [followers, setFollowers] = useState([]);          // meId's followers
  const [viewerFollowing, setViewerFollowing] = useState([]); // viewerId's following (for status pills)
  const [recv, setRecv] = useState([]); // requests received (viewer)
  const [sent, setSent] = useState([]); // requests sent (viewer)
  const [busy, setBusy] = useState({});
  const [subTab, setSubTab] = useState("received");

  const headers = useMemo(() => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token") || null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);
  const hasAuth = "Authorization" in headers;

  // Fetch meId's lists
  const fetchFollowing = async () => {
    if (!meId) return setFollowing([]);
    try {
      const { data } = await api.get(`/connections/following/${meId}`, { headers });
      setFollowing(normalizeList(data));
    } catch {
      setFollowing([]);
    }
  };
  const fetchFollowers = async () => {
    if (!meId) return setFollowers([]);
    try {
      const { data } = await api.get(`/connections/followers/${meId}`, { headers });
      setFollowers(normalizeList(data));
    } catch {
      setFollowers([]);
    }
  };

  // Fetch viewer's following for status display
  const fetchViewerFollowing = async () => {
    if (!viewerId) return setViewerFollowing([]);
    try {
      const { data } = await api.get(`/connections/following/${viewerId}`, { headers });
      setViewerFollowing(normalizeList(data));
    } catch {
      setViewerFollowing([]);
    }
  };

  // Requests (viewer)
  const fetchRequests = async () => {
    if (!hasAuth || !viewerId) {
      setRecv([]); setSent([]);
      return;
    }
    try {
      const rcv = await api.get(`/connections/requests`, { headers });
      setRecv(Array.isArray(rcv.data) ? rcv.data : []);
    } catch {
      setRecv([]);
    }
    try {
      const s =
        (await api
          .get(`/connections/requests/sent`, { headers })
          .catch(() => api.get(`/connections/sent`, { headers }))).data || [];
      setSent(Array.isArray(s) ? s : []);
    } catch {
      setSent([]);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchFollowing();
    fetchFollowers();
    fetchViewerFollowing();
    if (initialTab === "requests") fetchRequests();
    setTab(initialTab);
    setSubTab("received");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTab, meId, viewerId]);

  useEffect(() => {
    if (!open) return;
    if (tab === "requests") fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Utilities
  const viewerFollows = (id) =>
    viewerFollowing.some((u) => String(u.id) === String(id));

  // Destructive actions only when viewing your own connections
  const amOwner = viewerId && meId && String(viewerId) === String(meId);

  const removeFollowing = async (otherId) => {
    if (!amOwner || busy[otherId]) return;
    setBusy((b) => ({ ...b, [otherId]: true }));
    try {
      await api
        .delete(`/connections/following/${otherId}`, { headers })
        .catch(() => api.delete(`/connections/${otherId}`, { headers }))
        .catch(() => api.delete(`/connections`, { headers, data: { other_id: otherId } }));
      setFollowing((prev) => prev.filter((u) => String(u.id) !== String(otherId)));
      // also refresh viewerFollowing if you are the owner
      fetchViewerFollowing();
    } catch (e) {
      console.error("removeFollowing failed", e?.response?.data || e.message);
      alert("Couldn’t remove. Please try again.");
    } finally {
      setBusy((b) => ({ ...b, [otherId]: false }));
    }
  };

  const removeFollower = async (otherId) => {
    if (!amOwner || busy[otherId]) return;
    setBusy((b) => ({ ...b, [otherId]: true }));
    try {
      await api
        .delete(`/connections/followers/${otherId}`, { headers })
        .catch(() => api.delete(`/connections/${otherId}`, { headers }))
        .catch(() => api.delete(`/connections`, { headers, data: { other_id: otherId } }));
      setFollowers((prev) => prev.filter((u) => String(u.id) !== String(otherId)));
    } catch (e) {
      console.error("removeFollower failed", e?.response?.data || e.message);
      alert("Couldn’t remove follower. Please try again.");
    } finally {
      setBusy((b) => ({ ...b, [otherId]: false }));
    }
  };

  const followBack = async (otherId) => {
    if (!amOwner || busy[otherId]) return;
    setBusy((b) => ({ ...b, [otherId]: true }));
    try {
      const { data } = await api.post(
        `/connections/request`,
        { addressee_id: otherId },
        { headers }
      );
      if (data?.status === "accepted") {
        await fetchFollowing();
        await fetchViewerFollowing();
      }
    } catch (e) {
      console.error("followBack failed", e?.response?.data || e.message);
      alert("Couldn’t follow back. Please try again.");
    } finally {
      setBusy((b) => ({ ...b, [otherId]: false }));
    }
  };

  const first4Following = useMemo(() => following.slice(0, 4), [following]);
  const first4Followers = useMemo(() => followers.slice(0, 4), [followers]);

  if (!open) return null;

  // Dynamic copy for owner vs other
  const titleName = ownerName || "Connections";
  const followingEmpty =
    amOwner ? "You're not following anyone yet" : `${titleName} is not following anyone yet`;
  const followersEmpty =
    amOwner ? "No followers yet" : `${titleName} has no followers yet`;

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40">
      <div className="bg-white px-7 rounded-3xl shadow-[0_6px_0px_rgba(37,99,235,0.96)] w-full max-w-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between pt-5 pb-4">
          <h4 className="font-semibold text-lg">
            {viewerId != null &&
              meId != null &&
              String(viewerId) === String(meId)
                ? "Your Connections"
                : ownerName
                  ? `${ownerName}'s Connections`
                  : "Connections"}
          </h4>
          <button
            onClick={onClose}
            className="grid h-8 w-8 place-items-center text-blue-600"
            aria-label="Close"
          >
            <X size={22} weight="bold" />
          </button>
        </div>

        {/* Tabs */}
        <div className="">
          <div className="flex items-center gap-2">
            <TabButton active={tab === "overview"} onClick={() => setTab("overview")} label="Overview" />
            <TabButton active={tab === "following"} onClick={() => setTab("following")} label={`Following: ${following.length}`} />
            <TabButton active={tab === "followers"} onClick={() => setTab("followers")} label={`Followers: ${followers.length}`} />
            {amOwner && (
              <TabButton active={tab === "requests"} onClick={() => setTab("requests")} label="Requests" />
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-1 pb-6 pt-4">
          {/* OVERVIEW */}
          {tab === "overview" && (
            <div className="grid grid-cols-1 gap-8">
              {/* Following preview */}
              <section>
                <h4 className="text-sm text-gray-700 font-bold mb-3 ">Following</h4>
                {!listOnly && first4Following.length > 0 && (
                  <button type="button" onClick={() => setTab("following")} className="w-full text-left" title="View following">
                    <div className="flex items-center justify-between">
                      <div className="flex -space-x-3">
                        {first4Following.map((u) => (
                          <img
                            key={u.id}
                            src={toAvatar(u)}
                            alt={u.name || `User #${u.id}`}
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-white"
                          />
                        ))}
                      </div>
                      <span className="text-sm text-blue-600">
                        {following.length} following
                      </span>
                    </div>
                  </button>
                )}
                {(listOnly || first4Following.length === 0) && (
                  <button className="block text-blue-600 hover:font-bold text-sm" onClick={() => setTab("following")}>
                    {following.length} following
                  </button>
                )}
              </section>

              {/* Followers preview */}
              <section>
                <h4 className="text-sm text-gray-700 font-bold mb-3">Followers</h4>
                {!listOnly && first4Followers.length > 0 && (
                  <button type="button" onClick={() => setTab("followers")} className="w-full text-left" title="View followers">
                    <div className="flex items-center justify-between">
                      <div className="flex -space-x-3">
                        {first4Followers.map((u) => (
                          <img
                            key={u.id}
                            src={toAvatar(u)}
                            alt={u.name || `User #${u.id}`}
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-white"
                          />
                        ))}
                      </div>
                      <span className="text-sm text-blue-600 hover:underline">
                        {followers.length} follower{followers.length === 1 ? "" : "s"}
                      </span>
                    </div>
                  </button>
                )}
                {(listOnly || first4Followers.length === 0) && (
                  <button className="block text-blue-600 hover:underline text-sm" onClick={() => setTab("followers")}>
                    {followers.length} follower{followers.length === 1 ? "" : "s"}
                  </button>
                )}
              </section>
            </div>
          )}

          {/* FOLLOWING LIST */}
          {tab === "following" && (
            <UserList
              rows={following}
              emptyText={followingEmpty}
              rightRender={(u) => {
                // if viewing someone else's connections -> show YOUR status, not actions
                if (!amOwner) {
                  if (String(u.id) === String(viewerId)) return <span className="text-xs text-gray-400" />;
                  return viewerFollows(u.id) ? (
                    <span className="text-xs text-blue-600 px-2 py-1 rounded-md bg-blue-50">Connected</span>
                  ) : (
                    <span className="text-xs text-gray-500 px-2 py-1 rounded-md bg-gray-50">Not connected</span>
                  );
                }
                // owner view = original actions
                return (
                  <button
                    onClick={() => removeFollowing(u.id)}
                    disabled={busy[u.id]}
                    className="px-3 py-1.5 rounded-3xl text-sm bg-blue-300 text-blue-800 hover:opacity-90 disabled:opacity-70"
                  >
                    {busy[u.id] ? "…" : "Remove"}
                  </button>
                );
              }}
            />
          )}

          {/* FOLLOWERS LIST */}
          {tab === "followers" && (
            <UserList
              rows={followers}
              emptyText={followersEmpty}
              rightRender={(u) => {
                if (!amOwner) {
                  if (String(u.id) === String(viewerId)) return <span className="text-xs text-gray-400" />;
                  return viewerFollows(u.id) ? (
                    <span className="text-xs text-blue-600 px-2 py-1 rounded-md bg-blue-50">Connected</span>
                  ) : (
                    <span className="text-xs text-gray-500 px-2 py-1 rounded-md bg-gray-50">Not connected</span>
                  );
                }
                const iFollow = viewerFollows(u.id);
                return (
                  <div className="flex items-center gap-2">
                    {!iFollow ? (
                      <button
                        onClick={() => followBack(u.id)}
                        disabled={busy[u.id]}
                        className="px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70"
                      >
                        {busy[u.id] ? "…" : "Follow back"}
                      </button>
                    ) : (
                      <span className="text-sm text-blue-400 px-3 flex items-center gap-1">
                        <CheckCircle size={16} weight="bold" /> Connected
                      </span>
                    )}
                    <button
                      onClick={() => removeFollower(u.id)}
                      disabled={busy[u.id]}
                      className="px-4 py-1.5 text-sm rounded-3xl bg-blue-300 text-blue-800 hover:opacity-90 disabled:opacity-70"
                    >
                      {busy[u.id] ? "…" : "Remove"}
                    </button>
                  </div>
                );
              }}
            />
          )}

          {/* REQUESTS (only for owner) */}
          {tab === "requests" && amOwner && (
            <RequestsArea
              subTab={subTab}
              setSubTab={setSubTab}
              recv={recv}
              sent={sent}
              headers={headers}
              refresh={fetchRequests}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- tiny UI helpers ---------- */

function TabButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full font-medium text-sm ${
        active ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700"
      }`}
    >
      {label}
    </button>
  );
}

function SubTabButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full font-medium text-sm ${
        active ? "bg-yellow-200 text-blue-700" : "bg-yellow-100 text-blue-700"
      }`}
    >
      {label}
    </button>
  );
}


function UserList({ rows, emptyText, rightRender }) {
  if (!rows?.length) {
    return <p className="text-sm text-gray-500">{emptyText}</p>;
  }
  return (
    <ul className="divide-y divide-gray-50">
      {rows.map((u) => (
        <li key={u.id} className="py-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Link to={`/profile/${u.id}`}>
                <img
                  src={toAvatar(u)}
                  alt={u.name || `User #${u.id}`}
                  className="w-10 h-10 rounded-full object-cover"
                />
              </Link>
              <Link to={`/profile/${u.id}`} className="font-medium hover:text-blue-500 truncate">
                {u.name || u.username || `User #${u.id}`}
              </Link>
            </div>
            {rightRender?.(u)}
          </div>
        </li>
      ))}
    </ul>
  );
}

function RequestsArea({ subTab, setSubTab, recv, sent, headers, refresh }) {
  return (
    <div>
      <div className="flex items-center -ml-1 gap-2 mb-3">
        <SubTabButton active={subTab === "received"} onClick={() => setSubTab("received")} label={`Received (${recv.length})`} />
        <SubTabButton active={subTab === "sent"} onClick={() => setSubTab("sent")} label={`Sent (${sent.length})`} />
      </div>
      {subTab === "received" ? (
        <RequestsReceived rows={recv} headers={headers} onChanged={refresh} />
      ) : (
        <RequestsSent rows={sent} />
      )}
    </div>
  );
}

function RequestsReceived({ rows, headers, onChanged }) {
  const [busy, setBusy] = useState({});
  const accept = async (connectionId) => {
    setBusy((b) => ({ ...b, [connectionId]: true }));
    try {
      await api.post(`/connections/requests/${connectionId}/accept`, {}, { headers });
      onChanged?.();
    } catch (e) {
      console.error("Accept failed", e?.response?.data || e.message);
      alert("Couldn’t accept. Try again.");
    } finally {
      setBusy((b) => ({ ...b, [connectionId]: false }));
    }
  };
  const decline = async (connectionId) => {
    setBusy((b) => ({ ...b, [connectionId]: true }));
    try {
      await api.delete(`/connections/requests/${connectionId}`, { headers });
      onChanged?.();
    } catch (e) {
      console.error("Decline failed", e?.response?.data || e.message);
      alert("Couldn’t decline. Try again.");
    } finally {
      setBusy((b) => ({ ...b, [connectionId]: false }));
    }
  };

  if (!rows?.length) {
    return <p className="text-sm ml-3 text-gray-500">No pending requests</p>;
  }
  return (
    <ul className="divide-y divide-gray-50">
      {rows.map((r) => {
        const req = r.requester || r.sender || {};
        return (
          <li key={r.connection_id || r.id} className="py-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Link to={`/profile/${req.id || ""}`}>
                  <img
                    src={toAvatar(req)}
                    alt={req.name || `User #${req.id}`}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                </Link>
                <div className="min-w-0">
                  <p className="font-medium truncate">
                    {req.display_name ||
                      req.name ||
                      [req.first_name, req.last_name].filter(Boolean).join(" ") ||
                      req.username ||
                      `User ${req.id ?? ""}`}
                  </p>
                  <p className="text-xs text-gray-500">sent you a request</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => accept(r.connection_id || r.id)}
                  disabled={busy[r.connection_id || r.id]}
                  className="px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70"
                >
                  {busy[r.connection_id || r.id] ? "…" : "Accept"}
                </button>
                <button
                  onClick={() => decline(r.connection_id || r.id)}
                  disabled={busy[r.connection_id || r.id]}
                  className="px-3 py-1.5 rounded-md bg-blue-300 text-blue-800 hover:opacity-90 disabled:opacity-70"
                >
                  {busy[r.connection_id || r.id] ? "…" : "Decline"}
                </button>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function RequestsSent({ rows }) {
  if (!rows?.length) {
    return <p className="text-sm text-gray-500">No sent requests.</p>;
  }
  return (
    <ul className="divide-y divide-gray-50">
      {rows.map((r) => {
        const to = r.addressee || r.receiver || {};
        const status = r.status || "pending";
        return (
          <li key={r.connection_id || r.id} className="py-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Link to={`/profile/${to.id || ""}`}>
                  <img
                    src={toAvatar(to)}
                    alt={to.name || `User #${to.id}`}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                </Link>
                <div className="min-w-0">
                  <p className="font-medium truncate">
                    {to.display_name ||
                      to.name ||
                      [to.first_name, to.last_name].filter(Boolean).join(" ") ||
                      to.username ||
                      `User ${to.id ?? ""}`}
                  </p>
                  <p className="text-xs text-gray-500">
                    {status === "accepted" ? "Accepted" : "Pending"}
                  </p>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
